import React, { useEffect, useState } from 'react';
import useAcquisitionProviderFacade, { TAcquisitionProviderFacade } from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import { generateErrorMessage, generateMiscErrorMap, generatePersonalInformationErrorMap } from '../../utils/error-message';
import InlinePushup from '../../utils/utility-components/InlinePushup';
import { hasDefaultComponent } from '../../utils/pageModelUtils';
import sortPushupErrors from './sortPushupErrors';
import { useAllFieldSubscription } from '../../providers/ModernAcquisitionProvider/useSubscription';
import { isSamePersonCheckFromFacadePipe } from '../../utils/acquisition-utils';
import { authorizedUserFieldValidations } from './FieldValidations/CtaFieldValidations-AncillaryProducts';

export const useHandleErrorPushup = (): {
    InlineErrorMessages: () => JSX.Element
} => {

    const acquisitionProviderFacade = useAcquisitionProviderFacade();

    // subscription to all fields for UI changes
    const { fields } = useAllFieldSubscription();

    const [ messages, setMessages ] = useState([]);

    const acquisitionData = acquisitionProviderFacade.getPersonalInformation();
    const personalInfoErrors = acquisitionProviderFacade.getPersonalInfoErrors();
    const homeAddressPrimaryRef = acquisitionProviderFacade.getHomeAddressPrimaryRef();
    const creditProtectionInfo = acquisitionProviderFacade.getCreditProtectionInfo();
    const creditProtectionInfoValid = acquisitionProviderFacade.getIsCreditProtectionValid();
    const isAuthorisedUserSectionValid = acquisitionProviderFacade.getAuthorizedUser()?.isAuthorisedUserSectionValid;
    const authorisedUserValidity = acquisitionProviderFacade.getAuthorisedUserValidity()?.authorizedUserValidity;
    const { getFeatureFlags } = acquisitionProviderFacade.getCurrentMetaAcquisitionFlowState()
    const personalInformationErrorMap = generatePersonalInformationErrorMap(acquisitionData, personalInfoErrors, homeAddressPrimaryRef, getFeatureFlags);
    const miscErrorMap = generateMiscErrorMap(acquisitionData);

    const handleUpdatingErrorMessages = async (stateToValidate: TAcquisitionProviderFacade): Promise<void> => {
        // object to avoid duplicate entries
        const errorMessages = {};
        const acquisitionData = stateToValidate.getPersonalInformation();
        const isDobValid = 
            acquisitionData?.dateOfBirthDay?.state !== 'error' 
            && acquisitionData?.dateOfBirthMonth?.state !== 'error'
            && acquisitionData?.dateOfBirthYear?.state !== 'error';

        const hasSsnModule = hasDefaultComponent('cob-corp-acquisition/components/creditonebank/ssn-module');

        Object.entries(personalInformationErrorMap).map(
            ([key, message]) => {
                if ((key === 'ssn' && hasSsnModule) || key === 'monthlyIncome' ) {
                    return;
                }

                if (key === 'dob' && !isDobValid) {
                    errorMessages[key] = message;
                } else if (Object.prototype.hasOwnProperty.call(acquisitionData, key) && acquisitionData[key].state === 'error') {
                    errorMessages[key] = message;
                }
            },
        );

        if (hasSsnModule && !(stateToValidate.getSsn().isSsnInfoValid)) {
            errorMessages['ssn'] = personalInformationErrorMap.ssn;
        }

        const { isMonthlyIncomeValid, isMonthlyOrYearly } = stateToValidate.getMonthlyOrYearlyIncome();
        if (!isMonthlyIncomeValid) {
            if (isMonthlyOrYearly) {
                let toFind;
                let toReplace;

                if (isMonthlyOrYearly === 'monthly') {
                    toFind = 'Yearly';
                    toReplace = 'Monthly';
                } else {
                    toFind = 'Monthly';
                    toReplace = 'Yearly';
                }

                errorMessages['monthlyIncome'] = personalInformationErrorMap.monthlyIncome.replaceAll(toFind, toReplace);
            } else {
                errorMessages['monthlyIncome'] = personalInformationErrorMap.monthlyIncome;
            }
        }
        if (stateToValidate.getAuthorizedUser().authorizedUserInfo.IsAuthorizedUserSelected === true) {
            const isSamePerson = await isSamePersonCheckFromFacadePipe(acquisitionProviderFacade);
            if (isSamePerson.hasErrors()) {
                errorMessages['authorizedUserFirstName'] = generateErrorMessage(miscErrorMap.authorizedUserFirstName);
                errorMessages['authorizedUserLastName'] = generateErrorMessage(miscErrorMap.authorizedUserLastName);
                errorMessages['authorizedUserDob'] = miscErrorMap.authorizedUserDob;
            }
        }

        for await (const [key, message] of Object.entries(miscErrorMap)) {
            switch (key) {
                case 'authorizedUserFirstName':
                case 'authorizedUserLastName':
                    if (
                        acquisitionData?.[key]?.state === 'error'
                    ) {
                        errorMessages[key] = generateErrorMessage(message, !acquisitionData?.[key]?.value ? 'missingValue' : null);
                    }
                    break;
                case 'authorizedUserSpouse':
                    if (
                        acquisitionData?.[key]?.state === 'error'
                    ) {
                        errorMessages[key] = message;
                    }
                    break;
                case 'authorizedUserDob':
                    if (
                        acquisitionData?.['authorizedUserDateOfBirthDay']?.state === 'error' ||
                        acquisitionData?.['authorizedUserDateOfBirthMonth']?.state === 'error' ||
                        acquisitionData?.['authorizedUserDateOfBirthYear']?.state === 'error'
                    ) {
                        errorMessages[key] = message;
                    }
                    break;
                case 'cardDesign':
                    if (!stateToValidate.getIsSelectedCardValid()) {
                        errorMessages[key] = message;
                    }
                    break;
                case 'creditProtectionInitials':
                    if (!stateToValidate.getIsCreditProtectionValid()) {
                        errorMessages[key] = message;
                    }
                    break;
                default: break;
            }
        }

        setMessages(sortPushupErrors(errorMessages));
    };

    useEffect((): void => {
        handleUpdatingErrorMessages(acquisitionProviderFacade);
    }, [
        fields,
        creditProtectionInfo,
        creditProtectionInfoValid,
        authorisedUserValidity,
        isAuthorisedUserSectionValid,
        personalInfoErrors,
    ]);

    const InlineErrorMessages = (): JSX.Element => {
        if (!messages) {
            return null;
        }
        return <InlinePushup messages={messages} />
    }

    return {
        InlineErrorMessages
    }
};